/**
 * JDL — Correctif mobile (21/10/2026)
 * ------------------------------------------------------------
 * Problème : sur mobile, choisir une taille faisait remonter la page
 * jusqu'à la photo produit. Dawn appelle setActiveMedia(..., true) à chaque
 * changement de variante (chaque taille a son image côté TPOP) puis fait
 * window.scrollTo() vers cette image, qui est AU-DESSUS des boutons sur mobile.
 *
 * Correctif : sur mobile, un choix de TAILLE ne fait plus défiler la page
 * (l'image ne change pas pour le client). Un choix de COULEUR remonte toujours
 * vers la photo : le client doit voir le produit dans la couleur choisie.
 * Comportement desktop et clic sur miniature : inchangés (Dawn d'origine).
 * La rangée des tailles est repérée par l'attribut data-jdl-size-option
 * (posé dans snippets/product-variant-picker.liquid).
 */

/**
 * Décide s'il faut faire défiler la page jusqu'au média actif.
 * Fonction pure, testée dans media-gallery.test.js.
 * @param {Object} p
 * @param {boolean} p.fromVariantChange true si appelé suite à un choix de variante (prepend Dawn)
 * @param {boolean} p.sizeChange        true si la dernière option modifiée est la taille
 * @param {boolean} p.isDesktop         true si écran >= 750px
 * @param {number}  p.mediaTop          position haute du média dans la fenêtre (getBoundingClientRect().top)
 * @returns {boolean}
 */
function shouldScrollWindowToMedia({ fromVariantChange, sizeChange, isDesktop, mediaTop }) {
  // Mobile + choix de taille : le client est sur les boutons, on le laisse là.
  if (fromVariantChange && sizeChange && !isDesktop) return false;
  // Règle Dawn d'origine : pas de défilement si l'image est déjà visible.
  return mediaTop <= -0.5;
}

// Mémorise si la dernière option modifiée est la taille (écoute en capture, avant Dawn).
// `var` + garde : le fichier peut être réinjecté par les fenêtres « Choisir des options ».
var jdlLastChangeIsSize = jdlLastChangeIsSize || false;
if (typeof document !== 'undefined' && !window.__jdlSizeChangeListener) {
  window.__jdlSizeChangeListener = true;
  document.addEventListener(
    'change',
    (event) => {
      if (!event.target.closest || !event.target.closest('variant-selects')) return;
      jdlLastChangeIsSize = !!event.target.closest('[data-jdl-size-option]');
    },
    true
  );
}

if (typeof customElements !== 'undefined' && !customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            // Défilement HORIZONTAL du carrousel d'images : ne bouge pas la page.
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // JDL : décision centralisée (voir shouldScrollWindowToMedia en haut du fichier)
          const scroll = shouldScrollWindowToMedia({
            fromVariantChange: !!prepend,
            sizeChange: jdlLastChangeIsSize,
            isDesktop: this.mql.matches,
            mediaTop: activeMediaRect.top,
          });
          if (!scroll) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Export pour les tests Node uniquement (ignoré dans le navigateur).
if (typeof module === 'object' && module.exports) module.exports = { shouldScrollWindowToMedia };
